import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import * as Haptics from 'expo-haptics';
import { downloadMedia } from './mediaDownload';

// jszip is ~95 KB and only runs when someone downloads a bundled 3D model, so it is
// pulled in on demand rather than pinned into the first-load bundle. This has to be a
// dynamic `import()`: Metro resolves `require('literal')` statically wherever it appears,
// so an inline require would defer execution but still bundle the bytes.
const loadJSZip = async () => (await import('jszip')).default;

const isWeb = Platform.OS === 'web';

function getExt(url) {
  if (!url) return null;
  const cleaned = String(url).split('?')[0].split('#')[0];
  const ext = cleaned.split('.').pop()?.toLowerCase();
  return ext || null;
}

function findMtl(alternateFormats) {
  if (!Array.isArray(alternateFormats)) return null;
  return alternateFormats.find((f) => {
    const fmt = (f?.format || '').toLowerCase();
    if (fmt === 'mtl') return true;
    return getExt(f?.url) === 'mtl';
  })?.url || null;
}

// Same priority as Model3DViewer.findTextureUrl — Meshy PBR base color first,
// then Hunyuan-style `texture`, then any image alternate.
function findTextureEntry(alternateFormats) {
  if (!Array.isArray(alternateFormats)) return null;
  const baseColor = alternateFormats.find((f) => {
    const fmt = (f?.format || '').toLowerCase();
    return fmt === 'texture_basecolor' || fmt.startsWith('texture_basecolor_');
  });
  if (baseColor?.url) return baseColor;
  const named = alternateFormats.find((f) => (f?.format || '').toLowerCase() === 'texture');
  if (named?.url) return named;
  const img = alternateFormats.find((f) => {
    const mime = (f?.mimeType || '').toLowerCase();
    if (mime.startsWith('image/')) return true;
    const ext = getExt(f?.url);
    return ['png', 'jpg', 'jpeg', 'webp'].includes(ext);
  });
  return img || null;
}

async function fetchText(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
  return res.text();
}

async function fetchBuffer(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
  return res.arrayBuffer();
}

function sanitizeBaseName(name) {
  const cleaned = (name || `model-${Date.now()}`)
    .replace(/\.[a-zA-Z0-9]+$/, '') // strip trailing extension if user passed one
    .replace(/[^\w.-]+/g, '_');
  return cleaned || `model-${Date.now()}`;
}

/**
 * Download a 3D model with all sidecar files needed for the local previewer
 * (Preview.app, MeshLab, Blender, etc.) to render textures correctly.
 *
 * Why bundling matters:
 *   The OBJ format is geometry-only. Materials live in a sibling .mtl file
 *   referenced via `mtllib X.mtl`, and textures live in image files referenced
 *   via `map_Kd Y.png` inside that MTL. Providers like Hunyuan ship those
 *   three pieces at unrelated CDN paths, so downloading just the .obj leaves
 *   the user with an untextured white blob in their previewer.
 *
 * What we do for OBJ:
 *   - Fetch obj + mtl (if available) + texture image (if available)
 *   - Rewrite the OBJ's `mtllib` line to reference our renamed mtl
 *   - Rewrite the MTL's `map_Kd` lines to reference our renamed texture
 *   - Bundle all three into a zip with consistent filenames
 *   - Save the zip via the standard download / share flow
 *
 * For everything else (GLB/GLTF embed textures, USDZ is already a zip,
 * STL/PLY/FBX have no sidecar texture pipeline) we pass through to
 * `downloadMedia` unchanged.
 */
export async function download3DAsset({
  modelUrl,
  alternateFormats,
  baseName,
  options = {},
} = {}) {
  if (!modelUrl) {
    options?.onError?.('No model URL');
    return { success: false, message: 'No model URL' };
  }

  const primaryExt = getExt(modelUrl);
  const safeBase = sanitizeBaseName(baseName);

  // Only OBJ benefits from bundling.
  if (primaryExt !== 'obj') {
    return downloadMedia(modelUrl, `${safeBase}.${primaryExt || 'glb'}`, {
      // 3D models can't go to the Photos library on native — force share sheet.
      shareInstead: true,
      ...options,
    });
  }

  const mtlUrl = findMtl(alternateFormats);
  const textureEntry = findTextureEntry(alternateFormats);

  // Nothing to bundle — fall back to a plain OBJ download. The user will get
  // an untextured model in their previewer, but we've done all we can.
  if (!mtlUrl && !textureEntry) {
    return downloadMedia(modelUrl, `${safeBase}.obj`, {
      shareInstead: true,
      ...options,
    });
  }

  try {
    const [objText, mtlText, textureBuffer] = await Promise.all([
      fetchText(modelUrl),
      mtlUrl ? fetchText(mtlUrl) : Promise.resolve(null),
      textureEntry?.url ? fetchBuffer(textureEntry.url) : Promise.resolve(null),
    ]);

    const textureExt = textureEntry ? (getExt(textureEntry.url) || 'png') : null;
    const textureName = textureExt ? `${safeBase}.${textureExt}` : null;
    const mtlName = `${safeBase}.mtl`;
    const objName = `${safeBase}.obj`;

    // Rewrite OBJ → reference our renamed MTL (or strip the line entirely if
    // we couldn't fetch the MTL but have a texture-less geometry pack).
    let rewrittenObj = objText;
    const hasMtllib = /^mtllib\s+/m.test(objText);
    if (mtlText) {
      if (hasMtllib) {
        rewrittenObj = objText.replace(/^mtllib\s+.*$/gm, `mtllib ${mtlName}`);
      } else {
        rewrittenObj = `mtllib ${mtlName}\n${objText}`;
      }
    } else if (hasMtllib) {
      rewrittenObj = objText.replace(
        /^mtllib\s+.*$/gm,
        '# mtllib stripped — sidecar MTL not available',
      );
    }

    // Rewrite MTL's diffuse texture reference to point at our renamed texture
    // file. Other map types (map_Ks, map_Bump, etc.) stay as-is — they were
    // already broken before bundling and we don't try to fix them here.
    let rewrittenMtl = mtlText;
    if (rewrittenMtl && textureName) {
      rewrittenMtl = rewrittenMtl.replace(/^(map_Kd)\s+.*$/gim, `$1 ${textureName}`);
      // Some exporters emit lower-case `map_kd`; the regex above is /i so it
      // matches, but the replacement preserves whatever case was there via $1.
    }

    const JSZip = await loadJSZip();
    const zip = new JSZip();
    zip.file(objName, rewrittenObj);
    if (rewrittenMtl) zip.file(mtlName, rewrittenMtl);
    if (textureBuffer && textureName) zip.file(textureName, textureBuffer);

    const zipFilename = `${safeBase}.zip`;

    if (isWeb) {
      const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
      const url = URL.createObjectURL(blob);
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = zipFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        options?.onSuccess?.('Download started');
        return { success: true, message: 'Download started' };
      } finally {
        // Defer revoke so the browser actually starts the download first.
        setTimeout(() => URL.revokeObjectURL(url), 4000);
      }
    }

    // Native: write the zip into cache and open the share sheet so the user
    // can drop it into Files / Drive / Mail / etc.
    const base64 = await zip.generateAsync({
      type: 'base64',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 },
    });
    const localUri = `${FileSystem.cacheDirectory}${zipFilename}`;
    await FileSystem.writeAsStringAsync(localUri, base64, {
      encoding: FileSystem.EncodingType.Base64,
    });
    try { await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); } catch (_) { /* haptics optional */ }

    if (await Sharing.isAvailableAsync()) {
      await Sharing.shareAsync(localUri, {
        mimeType: 'application/zip',
        dialogTitle: 'Share 3D model',
        UTI: 'public.zip-archive',
      });
      // Best-effort cleanup — Sharing's promise resolves when the sheet opens,
      // not when the user dismisses, so delay the unlink.
      setTimeout(() => {
        FileSystem.deleteAsync(localUri, { idempotent: true }).catch(() => {});
      }, 60_000);
      options?.onSuccess?.('Saved');
      return { success: true, message: 'Saved' };
    }

    options?.onError?.('Sharing unavailable on this device');
    return { success: false, message: 'Sharing unavailable on this device' };
  } catch (err) {
    console.error('3D bundle download failed, falling back to plain OBJ', err);
    return downloadMedia(modelUrl, `${safeBase}.obj`, {
      shareInstead: true,
      ...options,
    });
  }
}
